import type {
  PrivateFeePolicy,
  FeeAction,
} from "../base-app/hooks/usePrivateFeePolicy";
import type { PrivateFeeQuote } from "./privateFees";
import { ethers } from "ethers";
let displayed: PrivateFeePolicy | null = null;
let approved: PrivateFeePolicy | null = null;
const refreshListeners = new Set<(contextKey: string) => void>();
export function subscribePrivateFeeRefresh(
  listener: (contextKey: string) => void,
) {
  refreshListeners.add(listener);
  return () => {
    refreshListeners.delete(listener);
  };
}
export function refreshPrivateFeePolicy(policy: PrivateFeePolicy | null) {
  if (policy?.contextKey)
    refreshListeners.forEach((listener) => listener(policy.contextKey!));
}
export function privateFeeApprovalFor(
  pool: string,
  token: string,
): PrivateFeePolicy {
  const policy = approved || displayed;
  if (
    !policy ||
    policy.pool.toLowerCase() !== pool.toLowerCase() ||
    ethers.getAddress(policy.token || ethers.ZeroAddress) !==
      ethers.getAddress(token) ||
    ethers.getAddress(policy.gasReimbursementToken) !== ethers.getAddress(token)
  )
    throw Error("Wait for the fee estimate before continuing.");
  return policy;
}
export function displayPrivateFeePolicy(policy: PrivateFeePolicy | null) {
  displayed = policy;
}
export function approvePrivateFeePolicy(policy: PrivateFeePolicy | null) {
  approved = policy;
}
export function approvedWithdrawalKind(
  amount: bigint,
  token = ethers.ZeroAddress,
): 1 | 3 {
  const policy = approved || displayed;
  return ethers.getAddress(policy?.token || ethers.ZeroAddress) ===
    ethers.getAddress(token) &&
    amount > 0n &&
    BigInt(policy?.feeCredit || "0") >= amount
    ? 3
    : 1;
}
export function checkPrivateFeeApproval(
  quote: PrivateFeeQuote,
  action: FeeAction,
  maintenance = false,
  policy = approved || displayed,
) {
  const rate = maintenance
    ? { protocolBps: 0, partnerBps: 0 }
    : policy?.rates[action];
  const matchesPool = policy?.pool.toLowerCase() === quote.pool.toLowerCase();
  if (
    !policy ||
    !rate ||
    !matchesPool ||
    ethers.getAddress(policy.token || ethers.ZeroAddress) !==
      ethers.getAddress(quote.request?.token || ethers.ZeroAddress) ||
    ethers.getAddress(policy.gasReimbursementToken) !==
      ethers.getAddress(quote.request?.token || ethers.ZeroAddress) ||
    Number(quote.terms[3]) !== rate.protocolBps ||
    Number(quote.terms[4]) !== rate.partnerBps ||
    BigInt(quote.amounts.gasReimbursement) >
      (action === "deposit" ? 0n : BigInt(policy.gasReimbursementAmount))
  ) {
    refreshPrivateFeePolicy(policy);
    throw Error("Fees changed. Review the refreshed amount and try again.");
  }
  const gross = BigInt(quote.amounts.grossAmount);
  if (
    BigInt(quote.amounts.protocolFee) !==
      (gross * BigInt(rate.protocolBps)) / 10000n ||
    BigInt(quote.amounts.partnerFee) !==
      (gross * BigInt(rate.partnerBps)) / 10000n
  )
    throw Error("Fee quote does not match the displayed rates.");
}
